import { useTranslation } from "@sagot/i18n";
import { Button, Icon, toast } from "@sagot/ui";
import { useErrorMessage } from "../../../../lib/api-errors";
import type { WorkflowOut } from "../types";
import { useWorkflowMutations } from "../use-workflows";
import { isHighImpactKey, wdOf } from "./defaults-model";

const D = "engage.workflows.defaults";

function ResumeButton({ workflow }: { workflow: WorkflowOut }) {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const { resume } = useWorkflowMutations(workflow.id);
  return (
    <Button
      size="sm"
      variant="secondary"
      loading={resume.isPending}
      aria-label={t(`${D}.banner.resumeLabel`, { name: workflow.name })}
      onClick={() => {
        resume.mutate(undefined, {
          onSuccess: () => { toast.success(t(`${D}.resumed`, { name: workflow.name })); },
          onError: (error) => { toast.error(message(error)); },
        });
      }}
    >
      <Icon name="play" />
      {t(`${D}.banner.resume`)}
    </Button>
  );
}

interface PausedBannerProps {
  paused: readonly WorkflowOut[];
  canPublish: boolean;
}

/** One paused high-impact default (clients or staff depend on it): its own red alert with what stops, never folded into the amber list. */
function StrongBanner({ workflow, canPublish }: { workflow: WorkflowOut; canPublish: boolean }) {
  const { t } = useTranslation();
  const wd = wdOf(workflow);
  return (
    <div className="rounded-lg border border-danger bg-danger-soft p-4" role="alert" data-testid="paused-banner-strong" data-key={workflow.system_key ?? undefined}>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <p className="flex min-w-0 items-start gap-2 text-sm">
          <Icon name="alert-octagon" className="mt-0.5 size-4 shrink-0 text-danger" />
          <span className="min-w-0">
            <span className="font-bold">{t(`${D}.service.strong.title`, { name: workflow.name, id: wd ?? workflow.system_key ?? "" })}.</span>
            {workflow.pause_impact ? <span className="text-fg-muted">{` ${workflow.pause_impact}`}</span> : null}
          </span>
        </p>
        {canPublish ? <ResumeButton workflow={workflow} /> : null}
      </div>
    </div>
  );
}

/** Above the section when a default that normally runs is paused: what is not being sent, and a way to turn it back on. */
export function PausedBanner({ paused, canPublish }: PausedBannerProps) {
  const { t } = useTranslation();
  if (paused.length === 0) return null;
  const strong = paused.filter((workflow) => isHighImpactKey(workflow.system_key));
  const regular = paused.filter((workflow) => !isHighImpactKey(workflow.system_key));
  return (
    <>
      {strong.map((workflow) => <StrongBanner key={workflow.id} workflow={workflow} canPublish={canPublish} />)}
      {regular.length > 0 ? (
        <div className="rounded-lg border border-warning bg-warning-soft p-4" role="status" data-testid="paused-banner">
          <p className="flex items-center gap-2 text-sm font-bold">
            <Icon name="alert-triangle" className="size-4 shrink-0 text-warning" />
            {t(`${D}.banner.title`, { count: regular.length })}
          </p>
          <ul className="mt-2 space-y-2">
            {regular.map((workflow) => (
              <li key={workflow.id} className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between" data-testid="paused-item">
                <p className="min-w-0 text-sm">
                  <span className="font-semibold">{workflow.name}</span>
                  {workflow.pause_impact ? <span className="text-fg-muted">{`: ${workflow.pause_impact}`}</span> : null}
                </p>
                {canPublish ? <ResumeButton workflow={workflow} /> : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </>
  );
}
